import { Link as RouterLink } from 'react-router-dom'
import { Empty, LayerCard, Text } from '@cloudflare/kumo'
import { CalendarBlankIcon, UserPlusIcon } from '@phosphor-icons/react'
import { useTranslation } from '../../i18n'
import { formatWeekdayDate } from '../../lib/format'
import type { Agenda, AgendaBookingItem, AgendaDay } from './agenda'
import { GuestRow } from './GuestRow'
import { onPlainClick } from './plain-click'

export interface HomeAgendaProps {
  agenda: Agenda
  /** The Next-up card is showing, so an empty agenda reads "nothing else". */
  hasNextUp: boolean
  /** Open the Invite sheet for a booking instead of navigating. */
  onInviteToBooking?: (booking: AgendaBookingItem['booking']) => void
}

function BookingRow({
  item,
  onInvite,
}: {
  item: AgendaBookingItem
  onInvite?: HomeAgendaProps['onInviteToBooking']
}) {
  const { t } = useTranslation()
  const { booking, startHm, endHm, guests } = item
  const meta = [
    t('dashboard.agenda.bookingMeta', { time: `${startHm} – ${endHm}` }),
    guests.length > 0
      ? t(guests.length === 1 ? 'dashboard.agenda.guestCountOne' : 'dashboard.agenda.guestCount', {
          count: guests.length,
        })
      : null,
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <li className="home-booking">
      <div className="home-row">
        <span className="home-avatar home-avatar--icon" aria-hidden>
          <CalendarBlankIcon size={18} />
        </span>
        <RouterLink
          to={`/my/bookings/${encodeURIComponent(booking.id)}/edit`}
          className="home-row__text home-row__link"
        >
          <span className="home-row__title">{booking.roomName ?? t('dashboard.nextUp.room')}</span>
          <span className="home-row__meta">{meta}</span>
        </RouterLink>
        <RouterLink
          to={`/my/guests/new?roomBookingId=${encodeURIComponent(booking.id)}`}
          onClick={onPlainClick(onInvite && (() => onInvite(booking)))}
          className="home-icon-link"
          aria-label={t('dashboard.agenda.inviteToBooking', {
            room: booking.roomName ?? t('dashboard.nextUp.room'),
          })}
        >
          <UserPlusIcon size={20} aria-hidden />
        </RouterLink>
      </div>
      {guests.length > 0 && (
        <ul className="home-list">
          {guests.map((inv) => (
            <GuestRow key={inv.id} invitation={inv} variant="nested" />
          ))}
        </ul>
      )}
    </li>
  )
}

export function DayGroup({
  day,
  onInvite,
}: {
  day: AgendaDay
  onInvite?: HomeAgendaProps['onInviteToBooking']
}) {
  const { t, locale } = useTranslation()
  const date = formatWeekdayDate(day.ymd, locale ?? 'pt-BR')
  const label =
    day.dayKind === 'today'
      ? `${t('dashboard.agenda.today')} · ${date}`
      : day.dayKind === 'tomorrow'
        ? `${t('dashboard.agenda.tomorrow')} · ${date}`
        : date

  return (
    <div className="flex flex-col gap-2">
      <h3 className="home-day-label">{label}</h3>
      <LayerCard>
        <ul className="home-list home-list--card">
          {day.items.map((item) =>
            item.kind === 'booking' ? (
              <BookingRow key={`b-${item.booking.id}`} item={item} onInvite={onInvite} />
            ) : (
              <GuestRow
                key={`i-${item.invitation.id}`}
                invitation={item.invitation}
                variant="standalone"
              />
            ),
          )}
        </ul>
      </LayerCard>
    </div>
  )
}

/** "Coming up": bookings and invitations as one agenda grouped by day. */
export function HomeAgenda({ agenda, hasNextUp, onInviteToBooking }: HomeAgendaProps) {
  const { t } = useTranslation()

  return (
    <section aria-labelledby="home-agenda" className="flex flex-col gap-3">
      <div className="home-section-head">
        <Text as="h2" variant="heading3" id="home-agenda">
          {t('dashboard.agenda.title')}
        </Text>
      </div>

      {agenda.days.length === 0 ? (
        <Empty
          size="sm"
          icon={<CalendarBlankIcon size={24} />}
          title={t(hasNextUp ? 'dashboard.agenda.emptyAfterNext' : 'dashboard.agenda.empty')}
          description={t('dashboard.agenda.emptyDescription')}
        />
      ) : (
        agenda.days.map((day) => <DayGroup key={day.ymd} day={day} onInvite={onInviteToBooking} />)
      )}

      <p className="home-see-all text-sm">
        {agenda.truncated && (
          <span className="text-kumo-subtle">
            {t('dashboard.agenda.more', { count: agenda.total - countShown(agenda) })}
          </span>
        )}
        <RouterLink to="/my/bookings" className="text-kumo-link font-medium">
          {t('dashboard.agenda.allBookings')}
        </RouterLink>
        <RouterLink to="/my/guests" className="text-kumo-link font-medium">
          {t('dashboard.agenda.allInvitations')}
        </RouterLink>
      </p>
    </section>
  )
}

function countShown(agenda: Agenda): number {
  return agenda.days.reduce((n, day) => n + day.items.length, 0)
}
